import { Component, ViewChild } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from "primeng/button";

import { BaseTableComponent } from 'src/app/layout/base-table-component';
import { OperationalFocusCategoriesList } from 'src/app/models/operationalFocusCategoriesModel';
import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';
import { MfgappsEventService } from 'src/app/services/mfgapps-event.service';
import { ActionButtonEvent, ColumnType, IconColumnModel, IconModel } from 'src/app/templates/table-template/tableModel';
import { TableTemplateComponent } from 'src/app/templates/table-template/table-template.component';
import { EntryListViewModel } from 'src/app/models/successEntryModel';
import { ExportSuccessModel } from 'src/app/models/exportSuccessModel';
import { ExportPowerpointComponent } from "../export-powerpoint/export-powerpoint.component";
import { FileModel } from 'src/app/models/uploadDocumentModel';
import { Constants } from 'src/app/models/constants';

@Component({
  selector: 'app-entries',
  imports: [TableTemplateComponent, MatProgressSpinnerModule, MatSnackBarModule, MatIcon, ButtonModule, ExportPowerpointComponent],
  templateUrl: './entries.component.html',
  styleUrl: './entries.component.scss'
})
export class EntriesComponent extends BaseTableComponent {
  @ViewChild(TableTemplateComponent) tableComponent!: TableTemplateComponent;
  loader = { isLoading: true, message: 'Loading...' };
  errorMessage = '';
  showActionIconsColumn = false; // Flag to show/hide Action icons column
  showAdditionalProductivityColumns = false; // Flag to show/hide Vehicles Saved and Type columns
  parser = new DOMParser();
  blockElements = new Set(['DIV', 'P', 'BR', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
  powerPointExportData: ExportSuccessModel;
  categoryIcons: IconModel[] = OperationalFocusCategoriesList.map(category => ({
    name: category.name, label: category.displayName, glyph: category.glyph,
    order: category.id, title: `${category.displayName}: ${category.title}`
  }));
  actionIcons: { [key: string]: IconModel } = {
    "Can Edit": { primeClass: 'pi-pencil', action: 'edit', label: 'Can Edit', title: 'Edit Entry', severity: 'warn', order: 1 },
    "Can Delete": { primeClass: 'pi-trash', action: 'delete', label: 'Can Delete', title: 'Delete Entry', severity: 'danger', order: 2 },
    "Can Download": { primeClass: 'pi-download', action: 'download', label: 'Can Download', title: 'Download Attachment', severity: 'help', order: 3 },
    "Can View": { primeClass: 'pi-eye', action: 'view', label: 'Can View', title: 'View Entry', severity: 'info', order: 4 },
    "Can Export": { primeClass: 'pi-file-export', action: 'export', label: 'Can Export', title: 'Export Entry to PowerPoint', severity: 'help', order: 5 }
  };
  
  constructor(router: Router, route: ActivatedRoute, private readonly mfgappsApiService: MfgappsApiService,
    private readonly eventService: MfgappsEventService) {
    super(router, route);
    this.columnList = [
      { name: "date", label: "Date", type: ColumnType.date, matchMode: 'dateBefore',
        dateFormat: 'MMM dd yyyy', dateOnly: true, colSize: 'date-compact', widthPercent: 12 },
      { name: "tm_name", label: "TM Name", type: ColumnType.category, matchMode: 'in', colSize: 'name-compact', widthPercent: 12 },
      { name: "company_code", label: "Company", type: ColumnType.category, matchMode: 'in', widthPercent: 8 },
      { name: "plant_name", label: "Plant", type: ColumnType.category, matchMode: 'in', colSize: 'mid', widthPercent: 10 },
      { name: "shop_name", label: "Shop", type: ColumnType.category, matchMode: 'in', colSize: 'mid', widthPercent: 10 },
      { name: "title", label: "Title", type: ColumnType.text, matchMode: 'contains', colSize: 'long', widthPercent: 20 },
      { name: "cost_avoidance", label: "Cost Avoidance", type: ColumnType.numeric, currency: 'USD', 
        matchMode: 'gt', colSize: 'mid', widthPercent: 16 },
      { name: "category", label: "Category", type: ColumnType.category, showIcon: true, matchMode: 'inArray',
        staticList: this.categoryIcons.map(category => `${category.glyph} ${category.label}`), colSize: 'category-compact',
        iconAlignment: 'center', filterField: 'category.textValue', sortField: 'category.sortNumber', widthPercent: 12 }
    ];
    if (this.showActionIconsColumn) {
      this.columnList.push(
        { name: "actionIcons", label: "Actions", type: ColumnType.category, showIcon: true, colSize: 'action-compact',
          iconAlignment: 'left', hideFilter: true, sortField: 'actionIcons.sortNumber', widthPercent: 10 });
    }
    if (this.showAdditionalProductivityColumns) {
      this.columnList.push(
        { name: "num_vehicles", label: "Vehicles Saved", type: ColumnType.numeric, matchMode: 'gt', colSize: 'mid' },
        { name: "productivityType", label: "Type", type: ColumnType.category, matchMode: 'in' });
    }
    this.multiSortMeta = [
      { field: 'date', order: -1 },
      { field: 'created_date', order: -1, hidden: true } // hidden field to use in sorting when dates are same
    ];
  }

  ngOnInit() {
    this.refreshData();
  }

  async refreshData() {
    this.loader.message = 'Loading Success Entries...';
    this.loader.isLoading = true;
    try {
      this.tableData = (await this.mfgappsApiService.getAllEntries()).result;
      this.processRecords();
      this.manageRouteParameters();
    }
    catch (error) {
      console.info(error);
      this.errorMessage = "Error occurred while fetching entries, Please try again !";
      this.eventService.showSnackBar(this.errorMessage, 'error');
    }
    this.resetLoader();
  }

  processRecords() {
    for (const entry of this.tableData) {
      this.eventService.setEntryProductivityType(entry);
      //Format TM name to show first name + first initial of last name (e.g., "John D")
      this.formatTMName(entry);
      //Category logic: set icons, sorting (sortNumber) and filter (textValue) values
      const category: IconColumnModel = { icons: [], sortNumber: 0, textValue: '' };
      for (const categoryIcon of this.categoryIcons) {
        if (entry[categoryIcon.name]) {
          this.addIconColumn(category, categoryIcon);
        }
      }
      entry.category = category;
      if (this.showActionIconsColumn) {
        //Action Icons logic: set icons, sorting (sortNumber) and filter (textValue) values
        const actionIcons: IconColumnModel = { icons: [], sortNumber: 0, textValue: '' };
        this.addIconColumn(actionIcons, this.actionIcons['Can View']);
        this.addIconColumn(actionIcons, this.actionIcons['Can Export']);
        if (this.eventService.checkUserAccessToEntry(entry)) {
          this.addIconColumn(actionIcons, this.actionIcons['Can Edit']);
          this.addIconColumn(actionIcons, this.actionIcons['Can Delete']);
        }
        entry.actionIcons = actionIcons;
      }
    }
  }

  addIconColumn(category: IconColumnModel, categoryIcon: IconModel) {
    category.icons.push(categoryIcon);
    if (categoryIcon.glyph) {
      category.textValue += categoryIcon.glyph;
    }
    category.textValue += ` ${categoryIcon.label}, `;
    category.sortNumber += categoryIcon.order ** 2;
  }

  formatTMName(entry: any) {
    //Format TM name to show first name + first initial of last name (e.g., "John D")
    if (entry.tm_name) {
      const parts = entry.tm_name.trim().split(' ');
      if (parts.length > 1) {
        entry.tm_name = `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}`;
      }
    }
  }

  async handleButtonClicked(event: ActionButtonEvent) {
    const entry: EntryListViewModel = event.entry;
    const action = event.customAction;
    if ((action === 'edit' || action === 'delete') && this.eventService.checkUserAccessToEntry(entry) === '') {
      this.eventService.showSnackBar(`You are not Authorized to ${action} this entry !!`, 'error');
      return;
    }
    if (action === 'edit') {
      this.router.navigate([`${Constants.EntryRoute}/${entry.entry_id}/edit`]);
    }
    else if (action === 'view' || action === 'rowClick') {
      this.router.navigate([`${Constants.EntryRoute}/${entry.entry_id}/view`]);
    }
    else if (action === 'download') {
      await this.downloadEntryFile(entry);
    }
    else if (action === 'export') {
      await this.initiateExport(entry);
    }
    else if (action === 'delete' && await this.eventService.confirmDialog(`Are you sure you want to delete entry <b>'${entry.title}'</b> ?`)) {
      await this.handleDeleteEntry(entry);
    }
  }

  goToNewSubmission() {
    this.router.navigate([`${Constants.EntryRoute}/new`]);
  }

  async initiateExport(entry: EntryListViewModel) {
    this.loader.message = 'Exporting Entry to PowerPoint...';
    this.powerPointExportData = new ExportSuccessModel();
    try {
      const apiPromises = [
        this.fetchEntryDetails(entry.entry_id),
        this.fetchEntryFiles(entry.entry_id)
      ];
      if (entry.productivity && !entry.data_type) {
        entry.fiscalyear = this.eventService.getFiscalYearFromDate(entry.date as any);
        apiPromises.push(this.fetchEntryShopConfig(entry), this.fetchEntryLineConfig(entry));
      }
      //Fetch entry details, files and configurations in parallel as all are independent of each other.
      const results = await Promise.allSettled(apiPromises);
      //Show error message if first API to fetch entry details fails as it is critical for export
      if (results[0].status !== 'fulfilled') {
        console.info('Fetching Entry details failed due to: ' + results[0].reason);
        this.eventService.showSnackBar("Export Entry failed, Please try again !", 'error');
      }
      else {
        this.powerPointExportData.exportInitiated = true;
      }
    }
    catch (error) {
      console.info(error);
      this.eventService.showSnackBar("Export Entry failed, Please try again !", 'error');
      this.onExportCompleted(false);
    }
    this.resetLoader();
  }

  async fetchEntryDetails(entryId: string) {
    const exportData = await this.mfgappsApiService.getEntryDetails(entryId);
    this.powerPointExportData.entry = exportData.result;
  }

  async fetchEntryShopConfig(entry: EntryListViewModel) {
    const shopItems: any[] = (await this.mfgappsApiService.getShopConfiguration(entry.fiscalyear, entry.company_id)).result;
    this.powerPointExportData.shopConfiguration = {};
    this.powerPointExportData.aggregatedDownTimeCosts = this.eventService.calculateAggregatedDowntimeCosts(shopItems, this.powerPointExportData.shopConfiguration);
  }

  async fetchEntryLineConfig(entry: EntryListViewModel) {
    const lineItems: any[] = (await this.mfgappsApiService.getLineConfiguration(entry.fiscalyear, entry.shop_id)).result;
    const lineConfiguration = {};
    for (const line of lineItems ?? []) {
      lineConfiguration[line.line_id] = line;
    }
    this.powerPointExportData.lineConfiguration = lineConfiguration;
  }

  async fetchEntryFiles(entryId: string) {
    try {
      const imageFileCategories = await this.eventService.getFileCategoryConfiguration();
      const uploadedFiles: FileModel[] = (await this.mfgappsApiService.getEntryFiles(entryId)).result;
      //Split files by categories and pass only image files for PowerPoint export if any images are found.
      this.eventService.assignFilesToCategories(uploadedFiles, imageFileCategories);
      this.powerPointExportData.imageFileCategories = imageFileCategories.filter(category => category.category_type === 'image/*');
    }
    catch (error) {
      console.info('Fetching Entry files failed due to: ' + error);
    }
  }

  onExportCompleted(exportSuccess: boolean) {
    this.powerPointExportData.exportInitiated = false;
    const exportPrefix = `Export Entry '${this.powerPointExportData.entry?.title}' to PowerPoint`;
    if (exportSuccess) {
      this.eventService.showSnackBar(`${exportPrefix} completed successfully !`, 'success');
    }
    else {
      this.eventService.showSnackBar(`${exportPrefix} could not complete !`, 'error');
    }
    this.powerPointExportData = null;
  }

  async handleDeleteEntry(entry: EntryListViewModel) {
    this.loader.message = 'Deleting Entry...';
    try {
      await this.mfgappsApiService.deleteEntry(entry.entry_id);
      this.eventService.showSnackBar(`Entry '${entry.title}' Deleted Successfully !`, 'success');
      //Instead of refreshing entire data, just remove deleted entry from table
      //this.refreshData();
      this.tableComponent.deleteEntry(entry);
    }
    catch (error) {
      console.info(error);
      const errorMessage = "Error occurred while deleting entry. Please try again !";
      this.eventService.showSnackBar(errorMessage, 'error');
    }
    this.resetLoader();
  }

  async downloadEntryFile(entry: EntryListViewModel) {
    this.loader.message = 'Downloading Entry Files...';
    const fileName = this.eventService.handleEntryFileName(entry);
    const fileResult = await this.mfgappsApiService.downloadEntryDocument(fileName, 'document', entry.entry_id);
    try {
      await this.eventService.simulateAnchorClick(fileResult.body, fileName);
      this.eventService.showSnackBar("File downloaded successfully !", 'success');
    }
    catch (error) {
      console.info(error);
      this.eventService.showSnackBar("Download failed, Please try again !", 'error');
    }
    this.resetLoader();
  }

  async exportEntries() {
    //The payload will be 'All' string if no filters or an array of visible entry Ids if filters applied
    const entryIds = this.tableComponent.filterPaginationText === '' ? 'All' 
      : this.tableComponent.getVisibleRowFieldValues('id');
    if (Array.isArray(entryIds) && entryIds.length === 0) {
      this.eventService.showSnackBar("No entries to export based on applied filters !", 'warn');
      return;
    }
    this.loader.message = 'Exporting Entries...';
    try {
      const exportData = await this.mfgappsApiService.exportEntries({ entries: entryIds });
      for (const eachEntry of exportData.result) {
        eachEntry.Actions = this.htmlToFormattedText(eachEntry.Actions) || '';
        eachEntry.Description = this.htmlToFormattedText(eachEntry.Description) || '';
        eachEntry.Outcomes = this.htmlToFormattedText(eachEntry.Outcomes) || '';
      }
      this.eventService.exportDataCSV(exportData.result, 'Sayka_SuccessEntries');
      this.eventService.showSnackBar("Entries exported successfully !", 'success');
    }
    catch (error) {
      console.info(error);
      this.eventService.showSnackBar("Export failed, Please try again !", 'error');
    }
    this.resetLoader();
  }

  htmlToFormattedText(html: string): string {
    const document = this.parser.parseFromString(html, 'text/html');
    const formattedText = this.traverse(document.body);
    return formattedText.replace(/[ \t]+/g, ' ').replace(/\n\s*\n/g, '\n').trim().replace(/^•/, ' •');
  }

  traverse(node: Node): string {
    let text = '';
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent || '';
      }
      else if (child.nodeType === Node.ELEMENT_NODE) {
        let childContent = this.traverse(child);
        //Replace all list items in both bulleted and numbered list with • character for CSV export
        if (child.nodeName === 'LI') {
          childContent = ` • ${childContent}`;
        }
        if (this.blockElements.has(child.nodeName)) {
          childContent = `\n${childContent}\n`;
        }
        text += childContent;
      }
    }
    return text;
  }

  resetLoader() {
    this.loader.isLoading = false;
    this.loader.message = '';
  }
}